import type { RobotCleaningProgressEntry } from '../../robotCleaningProgress/robotCleaningProgressSlice';
import { ENDED_TASK_STATUSES } from '../../robotCleaningProgress/utils/constants';
import type { IsCommandConfirmedOptions } from '../types/RobotCleaningControl.types';
import type { Optional } from 'lib/types/Optional';
import {
  BRangeCleaningCommandType,
  DeviceTaskStatusEnum,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

const hasTaskEnded = (
  task: Optional<RobotCleaningProgressEntry>,
  baseline: Optional<RobotCleaningProgressEntry>
): boolean =>
  !task?.deviceTaskId ||
  // A task that started after the command replaces the one it was sent to.
  (!!baseline?.deviceTaskId && task.deviceTaskId !== baseline.deviceTaskId) ||
  (!!task.taskStatus && ENDED_TASK_STATUSES.includes(task.taskStatus));

const hasRouteChanged = (
  task: Optional<RobotCleaningProgressEntry>,
  baseline: Optional<RobotCleaningProgressEntry>
): boolean =>
  (task?.step ?? null) !== (baseline?.step ?? null) ||
  (task?.name ?? null) !== (baseline?.name ?? null) ||
  (task?.mapId ?? null) !== (baseline?.mapId ?? null);

/**
 * Whether a command the API has queued has taken effect on the robot. The mutation only queues the
 * command, so the control buttons keep loading until the robot's real state shows the result: the
 * current task's status (B50 and CV50 share the task), or its route for a skip.
 *
 * Pause / Resume also accept the device's paused state: the task status can lag or stick (CV50 only
 * pauses its task on an interrupted report).
 *
 * Confirmed straight away when there is no task data to follow, so the buttons can't get stuck.
 */
export const isCommandConfirmed = ({
  commandType,
  model,
  baseline,
  task,
  isPaused,
}: IsCommandConfirmedOptions): boolean => {
  // Not known yet whether there is a task to follow.
  if (task?.status === 'loading') {
    return false;
  }

  const isTaskTracked = task?.status === 'open';

  switch (commandType) {
    case BRangeCleaningCommandType.MissionPause:
      return task?.taskStatus === DeviceTaskStatusEnum.Paused || isPaused || (model === 'B50' && !isTaskTracked);
    case BRangeCleaningCommandType.MissionResume:
      return task?.taskStatus === DeviceTaskStatusEnum.InProgress || !isPaused || (model === 'B50' && !isTaskTracked);
    case BRangeCleaningCommandType.MissionStop:
    case BRangeCleaningCommandType.MissionDriveBackAndCancel:
      return !isTaskTracked || hasTaskEnded(task, baseline);
    case BRangeCleaningCommandType.MissionDriveBackAndSkip:
      return !isTaskTracked || hasTaskEnded(task, baseline) || hasRouteChanged(task, baseline);
    default:
      return true;
  }
};
